import React, { useEffect, useMemo, useState } from "react";
import Nav from "../components/NavNotHome.jsx";
import Contact from "../components/form.jsx";
import Foot from "../components/footer.jsx";
import FetchAvailableRooms from "../components/roomavailable.jsx";
import Seo from "../components/Seo.jsx";
import { getDefaultPropertyConfig, normalizeProperty } from "../admin/config.js";
import { getProperty } from "../admin/properties.js";
import { fetchImagesByPath } from "../features/photos.jsx";
import {
  SITE_NAME,
  buildBreadcrumbStructuredData,
  buildPropertyDescription,
  buildPropertyKeywords,
  buildPropertyStructuredData,
} from "../seo.js";
import Slide from "../features/slide.jsx";
import "../slideshow.css";

function getPropertyRoute(propertyId) {
  if (propertyId === "Summer") {
    return "/Summer";
  }

  if (propertyId === "Main") {
    return "/Main";
  }

  return `/properties/${encodeURIComponent(propertyId)}`;
}

function PropertyInventoryPage({ propertyId }) {
  const fallbackProperty = useMemo(() => {
    const defaults = getDefaultPropertyConfig(propertyId) || {};
    return normalizeProperty(propertyId, defaults, []);
  }, [propertyId]);
  const [property, setProperty] = useState(fallbackProperty);
  const [unitSlides, setUnitSlides] = useState([]);

  useEffect(() => {
    let isMounted = true;

    async function loadPropertyDetails() {
      try {
        const nextProperty = await getProperty(propertyId);

        if (isMounted) {
          setProperty(nextProperty || fallbackProperty);
        }
      } catch (error) {
        if (isMounted) {
          setProperty(fallbackProperty);
        }
      }
    }

    loadPropertyDetails();

    return () => {
      isMounted = false;
    };
  }, [fallbackProperty, propertyId]);

  useEffect(() => {
    let isMounted = true;

    async function loadUnitSlides() {
      if (!property) {
        if (isMounted) {
          setUnitSlides([]);
        }

        return;
      }

      const nextUnitSlides = await Promise.all(
        property.units.map(async (unit) => {
          try {
            const images = await fetchImagesByPath(unit.documentPath);

            return {
              ...unit,
              images,
            };
          } catch (error) {
            return {
              ...unit,
              images: [],
            };
          }
        })
      );

      if (isMounted) {
        setUnitSlides(nextUnitSlides);
      }
    }

    loadUnitSlides();

    return () => {
      isMounted = false;
    };
  }, [property]);

  const activeProperty = property || fallbackProperty;
  const propertyPath = getPropertyRoute(propertyId);
  const propertyTitle = `${activeProperty?.label || "Property"} | ${SITE_NAME}`;
  const propertyDescription = buildPropertyDescription(activeProperty?.label || "this property");
  const propertyKeywords = buildPropertyKeywords(activeProperty?.label || SITE_NAME);

  return (
    <section className="inventory-page bg-light">
      <Seo
        description={propertyDescription}
        keywords={propertyKeywords}
        pathname={propertyPath}
        structuredData={[
          buildPropertyStructuredData(activeProperty, propertyPath),
          buildBreadcrumbStructuredData([
            { name: "Home", path: "/" },
            { name: activeProperty?.label || "Property", path: propertyPath },
          ]),
        ]}
        title={propertyTitle}
      />
      <div>
        <Nav />
      </div>

      <div className="inventory-page__hero text-center">
        <h2 className="fw-bolder">{property?.label || fallbackProperty?.label || "Property"}</h2>
        <p className="lead fw-normal text-muted mb-0">Serving the Wolfville area</p>
      </div>

      <div className="slides-container">
        {unitSlides.length ? (
          unitSlides.map((unit) => (
            <div className="divcenter" key={unit.id}>
              <Slide
                images={unit.images}
                navPosition="bottom"
                title={unit.label}
              />
              <div className="tgt">
                <a className="btn btn-primary mb-3 ms-1" href="#contact">
                  Inquire
                </a>
                <FetchAvailableRooms propertyId={property.id} unitId={unit.id} />
              </div>
            </div>
          ))
        ) : (
          <div className="inventory-page__empty">Units will appear here soon.</div>
        )}
      </div>

      <div>
        <Contact />
      </div>
      <div>
        <Foot />
      </div>
    </section>
  );
}

export default PropertyInventoryPage;
